import * as types from "./types";
import * as selectors from "./selectors";
import { actions as localeActions } from "../locale";
import {
  actions as routerActions,
  selectors as routerSelectors
} from "../router";
import { selectors as brandSelectors } from "../brand";
import { actions as bridgeActions } from "../bridge";
import { selectors as userSelectors } from "../user";
import { actions as storageActions } from "../storage";
import { actions as modalActions } from "../modals";
import JigsawAPI from "../../services/jigsaw";
import { isRelativeURL, safeRedirect } from "../../ui/utils/location";
import { setItem } from "../../ui/utils/data";

export const loadUserBegin = () => ({
  type: types.GET_USER_REQUEST
});

export const userLoaded = userInfo => ({
  type: types.GET_USER_SUCCESS,
  userInfo
});

export const userSignedOut = () => ({
  type: types.USER_SIGNED_OUT,
  isFetching: false
});

export const loadUser = (forceUpdate = false) => async (dispatch, getState) => {
  const state = getState();
  if (forceUpdate || selectors.shouldLoadUser(state)) {
    dispatch(loadUserBegin());
    return JigsawAPI.User.getInfo()
      .then(({ user }) => dispatch(userLoaded(user)))
      .catch(() => dispatch(userSignedOut()));
  }
};

const setUserInfoRequest = info => ({
  type: types.SET_USER_INFO_REQUEST
});

const setUserInfoSuccess = userInfo => ({
  type: types.SET_USER_INFO_SUCCESS,
  userInfo
});

const setUserInfoFailure = error => ({
  type: types.SET_USER_INFO_FAILURE,
  error
});

const setUserInfo = (info, dispatch) => {
  dispatch(setUserInfoRequest(info));
  return JigsawAPI.User.setInfo(info)
    .then(data => {
      dispatch(setUserInfoSuccess(data));
      return { success: true, data };
    })
    .catch(error => {
      dispatch(setUserInfoFailure(error));
      return { success: false, error };
    });
};

const resetPasswordRequest = () => ({
  type: types.RESET_PASSWORD_REQUEST
});
const resetPasswordSuccess = () => ({
  type: types.RESET_PASSWORD_SUCCESS
});
const resetPasswordFailure = error => ({
  type: types.RESET_PASSWORD_FAILURE,
  error
});

export const resetPassword = (email, updateUrl) => (dispatch, getState) => {
  if (selectors.shouldResetPassword(getState())) {
    dispatch(resetPasswordRequest());
    const domain = "vitalsource.com";
    return JigsawAPI.forgotPassword({ email, domain, updateUrl })
      .then(() => {
        dispatch(resetPasswordSuccess());
        return { success: true };
      })
      .catch(error => {
        dispatch(resetPasswordFailure(error));
        return { success: false };
      });
  }
};

const updateForgottenPasswordRequest = () => ({
  type: types.UPDATE_FORGOTTEN_PASSWORD_REQUEST
});
const updateForgottenPasswordSuccess = () => ({
  type: types.UPDATE_FORGOTTEN_PASSWORD_SUCCESS
});
const updateForgottenPasswordFailure = error => ({
  type: types.UPDATE_FORGOTTEN_PASSWORD_FAILURE,
  error
});

export const updateForgottenPassword = (token, password) => (
  dispatch,
  getState
) => {
  if (token && password) {
    dispatch(updateForgottenPasswordRequest());
    return JigsawAPI.updateForgottenPassword({
      token,
      password
    })
      .then(() => {
        // If the user is already authenticated, we need to update
        // the user information to respect changes to strong password, etc.
        const state = getState();
        if (userSelectors.isAuthenticated(state)) {
          return dispatch(loadUser(true));
        }
      })
      .then(() => {
        dispatch(updateForgottenPasswordSuccess());
        return { success: true };
      })
      .catch(error => {
        dispatch(updateForgottenPasswordFailure(error));
        return { success: false, error };
      });
  }
};

const createUserRequest = obj => ({
  type: types.CREATE_USER_REQUEST
});
const createUserSuccess = info => ({
  type: types.CREATE_USER_SUCCESS,
  userInfo: info
});
const createUserFailure = error => ({
  type: types.CREATE_USER_FAILURE,
  error
});
const shouldCreateNewUser = state => !selectors.isCreatingUser(state);

export const createUser = userObject => (dispatch, getState) => {
  if (shouldCreateNewUser(getState())) {
    dispatch(createUserRequest(userObject));
    return JigsawAPI.User.create(userObject)
      .then(data => dispatch(createUserSuccess(data)))
      .catch(error => dispatch(createUserFailure(error)));
  }
  return Promise.reject("User already being created.");
};

const mergeAccountRequest = () => ({
  type: types.MERGE_ACCOUNT_REQUEST
});
const mergeAccountSuccess = info => ({
  type: types.MERGE_ACCOUNT_SUCCESS,
  userInfo: info
});
const mergeAccountFailure = error => ({
  type: types.MERGE_ACCOUNT_FAILURE,
  error
});

export const mergeAccount = (email, password) => (dispatch, getState) => {
  if (selectors.shouldMergeUser(getState())) {
    dispatch(mergeAccountRequest());
    return JigsawAPI.User.mergeAccount({ email, password })
      .then(data => {
        dispatch(
          mergeAccountSuccess({
            ...data,
            needsBookListUpdate: true,
            ssoOnly: false
          })
        );
        return { success: true };
      })
      .catch(error => {
        dispatch(mergeAccountFailure(error));
        return { success: false };
      });
  }
  return { success: false };
};

const emailExistsRequest = email => ({
  type: types.EMAIL_EXISTS_REQUEST,
  email
});
const emailExistsSuccess = (email, exists = false, approved = false) => ({
  type: types.EMAIL_EXISTS_SUCCESS,
  email,
  exists,
  approved
});
const emailExistsFailure = (email, error) => ({
  type: types.EMAIL_EXISTS_FAILURE,
  email,
  error
});
const shouldCheckIfEmailExists = (state, email) =>
  !selectors.isCheckingEmailExists(state, email);

export const emailExists = email => (dispatch, getState) => {
  if (shouldCheckIfEmailExists(getState(), email)) {
    dispatch(emailExistsRequest(email));
    return JigsawAPI.User.exists(email)
      .then(({ exists, approved }) => {
        dispatch(emailExistsSuccess(email, exists, approved));
        return { exists, approved };
      })
      .catch(error => dispatch(emailExistsFailure(email, error)));
  }
  return Promise.reject("User already being created.");
};

const resendActivationEmailRequest = email => ({
  type: types.RESEND_ACTIVATION_EMAIL_REQUEST,
  email
});
const resendActivationEmailSuccess = email => ({
  type: types.RESEND_ACTIVATION_EMAIL_SUCCESS,
  email
});
const resendActivationEmailFailure = (email, error) => ({
  type: types.RESEND_ACTIVATION_EMAIL_FAILURE,
  email,
  error
});
const shouldResendActivationEmail = (state, email) =>
  !selectors.isResendingActivationEmail(state, email);

export const resendActivationEmail = (email, url) => (dispatch, getState) => {
  if (shouldResendActivationEmail(getState(), email)) {
    dispatch(resendActivationEmailRequest(email));
    return JigsawAPI.User.resendActivationEmail({ email, url })
      .then(data => {
        dispatch(resendActivationEmailSuccess(email));
        return true;
      })
      .catch(error => {
        dispatch(resendActivationEmailFailure(email, error));
        return Promise.reject();
      });
  }
  return Promise.reject("User already being created.");
};

export const setInfo = info => async dispatch => setUserInfo(info, dispatch);

export const setLocale = locale => async dispatch =>
  setUserInfo({ locale }, dispatch).then(() => {
    dispatch(localeActions.fetchLocale(locale));
    // Set language on login so that a logged out user will reflect the last language chosen
    dispatch(
      storageActions.setKey("updateLanguageOnLogin", locale, {
        userScope: false
      })
    );
  });

export const signOut = (user, dest) => async dispatch => {
  // await new Promise((resolve) => JigsawAPI.User.signOut(() => resolve()));
  // const userInfo = await JigsawAPI.User.getInfo();
  await JigsawAPI.User.signOut();
  bridgeActions.logout();

  // clear redirect
  // This needs to be here instead of the legacy fraction middleware because a 401 on the
  // signin page was clearing this out.... maybe we should only fire those on 401s if we
  // think you're signed in.
  Fraction.redirectAfterSignIn = undefined;
  setItem("redirectAfterSignIn", JSON.stringify(Fraction.redirectAfterSignIn));
  Fraction.queryAfterSignIn = undefined;
  setItem("queryAfterSignIn", JSON.stringify(Fraction.queryAfterSignIn));

  dispatch(userSignedOut());

  dest = Fraction.Brand.Locations.signout_redirect || dest || "/";
  if (Fraction.Brand.Alohomora.enabled) {
    if (isRelativeURL(dest)) {
      window.location = `https://${
        Fraction.Brand.Alohomora.host
      }/logout?redirect_uri=${`${window.location.origin}${dest}`}&brand=${
        window.location.hostname
      }`;
    } else {
      window.location = `https://${Fraction.Brand.Alohomora.host}/logout?redirect_uri=${Fraction.Brand.Locations.signout_redirect}&brand=${window.location.hostname}`;
    }
  } else {
    if (isRelativeURL(dest)) {
      dispatch(routerActions.go(dest));
    } else {
      safeRedirect(dest);
    }
  }
};

const requestEmailVerificationRequest = () => ({
  type: types.REQUEST_EMAIL_VERIFICATION_REQUEST
});
const requestEmailVerificationSuccess = () => ({
  type: types.REQUEST_EMAIL_VERIFICATION_SUCCESS
});
const requestEmailVerificationFailure = error => ({
  type: types.REQUEST_EMAIL_VERIFICATION_FAILURE,
  error
});
const shouldRequestEmailVerification = state =>
  !selectors.isRequestingEmailVerification(state);

export const requestEmailVerification = () => (dispatch, getState) => {
  const state = getState();
  if (shouldRequestEmailVerification(state)) {
    dispatch(requestEmailVerificationRequest());
    const brandName = brandSelectors.getBrandValue(state, "brand", "Bookshelf");
    const redirect = `${routerSelectors.getRootRoute(state)}/verify`;
    const error = `${redirect}?expired=true`;
    return JigsawAPI.User.requestEmailVerification({
      brandName,
      redirect,
      error
    })
      .then(data => {
        dispatch(requestEmailVerificationSuccess());
        return true;
      })
      .catch(error => {
        dispatch(requestEmailVerificationFailure(error));
        return false;
      });
  }
  return Promise.reject("Verification email already being sent.");
};
